import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {MessageService, PrimeTemplate} from "primeng/api";
import {TranslateService} from "@ngx-translate/core";
import {IColumns, DEFAULT_DATE_FORMAT, Utils,
  SvgLoaderComponent, CustomTableHeader} from "lib-base";
import {AsidePublicComponent} from "../../aside-public/aside-public.component";
import {ButtonDirective} from "primeng/button";
import {DatePipe, NgIf, TitleCasePipe} from "@angular/common";
import {DividerModule} from "primeng/divider";
import {InputTextModule} from "primeng/inputtext";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {SkeletonModule} from "primeng/skeleton";
import {TableModule} from "primeng/table";
import {TagModule} from "primeng/tag";
import {firstValueFrom, forkJoin, lastValueFrom} from "rxjs";
import {SolicitudPqrsdService} from "../../../../services/pqrsd/solicitud-pqrsd.service";
import {SolicitudPqrsdActividadesService} from "../../../../services/pqrsd/solicitud-pqrsd-actividades.service";
import {IGetDetalleSolicitud, IGetSolicitudActividades} from "../../../../shared/interfaces/pqrsdf";

@Component({
  selector: 'app-estado-pqrsd',
  standalone: true,
  imports: [
    AsidePublicComponent,
    ButtonDirective,
    DatePipe,
    DividerModule,
    InputTextModule,
    NgIf,
    PrimeTemplate,
    ReactiveFormsModule,
    SkeletonModule,
    TableModule,
    TagModule,
    FormsModule,
    CustomTableHeader,
    SvgLoaderComponent
  ],
  templateUrl: './estado-pqrsd.component.html',
  styleUrl: './estado-pqrsd.component.scss'
})
export class EstadoPqrsdComponent implements OnInit {
  columns!: IColumns[];
  translations: any = {};
  size: number = 5;
  radicado: string;
  tramite: string = '';
  detalleSolicitud!: IGetDetalleSolicitud | null;
  actividades: IGetSolicitudActividades [] = [];
  loadingSolicitud: boolean = false;
  loadingActividades: boolean = false;
  readonly defaultDateFormat = DEFAULT_DATE_FORMAT;
  private basePath: string = '';

  constructor(private _ar: ActivatedRoute,
              private _ms: MessageService,
              private _t: TranslateService,
              private _solicitudPqrsdActividadesService:SolicitudPqrsdActividadesService,
              private router: Router,
              private solicitudService: SolicitudPqrsdService) {
    this.radicado = this._ar.snapshot.params['radicado'] ?? '';
  }

  async ngOnInit(): Promise<void> {
    await this.translate();
    this.configureColumns();
    if (this.radicado) this.buscar();
    this._ar.parent?.url.subscribe((url) => {
        this.basePath = url[0]?.path;
    });

  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      titleMain: this._t.get('components.app-estado-pqrsd.title-main'),
      subtitleMain: this._t.get('components.app-estado-pqrsd.subtitle-main'),
      titleCardMain: this._t.get('components.app-estado-pqrsd.title-card-main'),
      dependencia: this._t.get('common.dependencia'),
      estadoTramite: this._t.get('components.app-estado-pqrsd.estado-tramite'),
      fechaTramite: this._t.get('components.app-estado-pqrsd.fecha-tramite'),
      horaTramite: this._t.get('components.app-estado-pqrsd.hora-tramite'),
      volverConsultar: this._t.get('components.app-estado-pqrsd.volver-a-consultar'),
      consultar: this._t.get('components.app-estado-pqrsd.consultar'),
      actividadesDelTramite: this._t.get('components.app-estado-pqrsd.actividades-del-tramite'),
      busquedaTramite: this._t.get('components.app-estado-pqrsd.busqueda-tramite'),
      tuTramite: this._t.get('components.app-estado-pqrsd.tu-tramite'),
      buscarTramite: this._t.get('components.app-estado-pqrsd.buscar-tramite'),

      actividad: this._t.get('common.actividad'),
      usuarioAsignado: this._t.get('common.usuario-asignado'),
      estado: this._t.get('common.estado'),
      fechaFinal: this._t.get('common.fecha-final'),
      noHayRegistros: this._t.get('msg.no-hay-registros'),
      noHayRegistrosDetalle: this._t.get('common.valide-consulta'),
      completeCampos: this._t.get('form.msg.complete-campos'),
      warnSummary: this._t.get('form.msg.warn-summary'),
    }));
  }

  buscar() {
    if (!this.radicado) {
      this._ms.add({
        severity: 'warn',
        summary: this.translations.warnSummary,
        detail: this.translations.completeCampos
      });
      return;
    }
    this.getActividadesByRadicado(this.radicado)
    this.getSolicitudByRadicado(this.radicado)
  }

  getActividadesByRadicado(radicado: string) {
    this.loadingActividades = true;
    lastValueFrom(this._solicitudPqrsdActividadesService.getActividadesByRadicado(this.radicado))
      .then((data) => {
        this.actividades = data;
        this.loadingActividades = false;
      }).catch((error) => {
      this.loadingActividades = false;
    });
  }

  getSolicitudByRadicado(radicado: string) {
    this.loadingSolicitud = true;
    lastValueFrom(this.solicitudService.getDetalleSolicitud(this.radicado))
      .then((data) => {
        this.detalleSolicitud = data;
        this.loadingSolicitud = false;
      }).catch((error) => {
      this.loadingSolicitud = false;
      this.detalleSolicitud = null;
    });
  }

  configureColumns() {
    this.columns = [
      {
        id: 'rowindex-column',
        field: '',
        header: 'No.',
        sort: false,
        columnFilter: false,
        class: 'text-left pl-0 color-blue-2 border-blue-2 border-none border-bottom-2 border-solid'
      },
      {
        id: 'actividad-column',
        field: 'actividad',
        header: this.translations.actividad,
        sort: false,
        columnFilter: false,
        class: 'text-left color-blue-2 border-blue-2 border-none border-bottom-2 border-solid'
      },
      {
        id: 'usuarioAsignado-column',
        field: 'usuarioAsignado',
        header: this.translations.usuarioAsignado,
        sort: false,
        columnFilter: false,
        class: 'text-left color-blue-2 border-blue-2 border-none border-bottom-2 border-solid'
      },
      {
        id: 'estado-column',
        field: 'estado',
        header: this.translations.estado,
        sort: false,
        columnFilter: false,
        class: 'text-left color-blue-2 border-blue-2 border-none border-bottom-2 border-solid'
      },
      {
        id: 'fecha-final-column',
        field: 'fechaFinal',
        header: this.translations.fechaFinal,
        sort: false,
        columnFilter: false,
        class: 'text-left color-blue-2 border-blue-2 border-none border-bottom-2 border-solid'
      }
    ];
  }
  searchTramites() {
    if (!this.tramite) {
      this._ms.add({
        severity: 'warn',
        summary: this.translations.warnSummary,
        detail: this.translations.completeCampos
      });
      return;
    }
    this.router.navigate([this.basePath, 'consulta', this.tramite]).then();

  }
  getTramiteEstadoTagIcon(estado: string, usuarioAsignadoCorreo: string): string {
    return Utils.getTramiteEstadoTagIcon(estado, usuarioAsignadoCorreo);
  }

  getTramiteEstadoTagSeverity(estado: string, usuarioAsignadoCorreo: string): 'success' | 'secondary' | 'info' | 'warning' | 'danger' | 'contrast' {
    return  Utils.getTramiteEstadoTagSeverity(estado, usuarioAsignadoCorreo);
  }


}
